import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import StatusBadge from "@/components/StatusBadge";
import { SERVICE_BY_KEY, SERVICES } from "@/lib/services";
import { formatDate } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowUpRight, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const FILTERS = ["all","pending","approved","in_progress","completed","rejected"];

export default function ProjectsList() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [service, setService] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = projects.filter(p =>
    (filter === "all" || p.status === filter) &&
    (service === "all" || p.service_type === service) &&
    (!search || p.title.toLowerCase().includes(search.toLowerCase()) || p.description.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <AppShell subtitle="§ PROJECTS" title="All projects">
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search projects…" className="pl-9 h-10 rounded-md" data-testid="projects-search" />
        </div>
        <select value={service} onChange={(e)=>setService(e.target.value)} className="h-10 px-3 border border-black/10 rounded-md text-sm bg-white" data-testid="projects-service-filter">
          <option value="all">All services</option>
          {SERVICES.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
        </select>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList className="bg-white border border-black/10 rounded-md h-auto p-1 flex-wrap">
          {FILTERS.map(f => (
            <TabsTrigger key={f} value={f} className="fx-mono-tag data-[state=active]:bg-black data-[state=active]:text-white rounded-md px-3 py-1.5" data-testid={`projects-filter-${f}`}>
              {f.replace("_"," ").toUpperCase()}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="border border-black/10 p-10 text-center text-neutral-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-black/20 p-16 text-center">
          <div className="fx-mono-tag mb-2">NO MATCHES</div>
          <div className="text-lg text-neutral-600">Try a different filter or search term.</div>
        </div>
      ) : (
        <div className="border border-black/10">
          <div className="grid grid-cols-12 border-b border-black/10 bg-[#F4F6F8] px-5 py-3 fx-mono-tag">
            <div className="col-span-5">PROJECT</div>
            <div className="col-span-2">SERVICE</div>
            <div className="col-span-2">SUBMITTED</div>
            <div className="col-span-2">STATUS</div>
            <div className="col-span-1"></div>
          </div>
          {filtered.map(p => {
            const svc = SERVICE_BY_KEY[p.service_type];
            return (
              <Link to={`/dashboard/projects/${p.id}`} key={p.id} className="grid grid-cols-12 px-5 py-4 items-center border-b border-black/5 last:border-b-0 hover:bg-[#F8FAFC] transition-colors" data-testid={`projects-row-${p.id}`}>
                <div className="col-span-5">
                  <div className="font-semibold text-sm tracking-tight line-clamp-1">{p.title}</div>
                  <div className="text-xs text-neutral-500 mt-0.5 line-clamp-1">{p.description}</div>
                </div>
                <div className="col-span-2 text-sm">{svc?.name}</div>
                <div className="col-span-2 text-sm font-mono text-neutral-600">{formatDate(p.created_at)}</div>
                <div className="col-span-2"><StatusBadge status={p.status} /></div>
                <div className="col-span-1 flex justify-end"><ArrowUpRight size={14} weight="bold" className="text-neutral-400" /></div>
              </Link>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
